import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IPromocodeReward, PromoStatus, StateActionStatus } from '@dev-fast/types';
import { Observable, Subject, takeUntil } from 'rxjs';

import { DEFAULT_PROMO } from './constants/background.const';
import { PROMOCODE_ENGINE, PromocodeEngine } from './symbols';
import { BonusAmount, IPromocodeForm } from './types';
import { bonusAmountConstructorUtil, errorMessageConstructorUtil } from './utils';

@Component({
  selector: 'app-promocode',
  templateUrl: './promocode.component.html',
  styleUrls: ['./promocode.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromocodeComponent implements OnInit, OnDestroy {
  @Input() defaultPromocode = DEFAULT_PROMO;
  @Input() isCompact = false;
  @Input() minPayment: number | undefined;
  @Output() onpromocodeApply: EventEmitter<IPromocodeReward | null> = new EventEmitter();
  private destroy$: Subject<void> = new Subject();
  private lastAppliedPromo = '';
  promoStatus: PromoStatus = PromoStatus.DEFAULT;
  promoStatusEnum: typeof PromoStatus = PromoStatus;
  applyActionStatus$: Observable<StateActionStatus.DISPATCH | StateActionStatus.SUCCESS> = this.promocodeService.applyActionStatus$;
  reward: IPromocodeReward | null = null;
  errorMessage: string | undefined;

  promocodeForm: FormGroup<IPromocodeForm> = new FormGroup<IPromocodeForm>({
    code: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(1)],
    }),
  });

  constructor(
    @Inject(PROMOCODE_ENGINE) private promocodeService: PromocodeEngine,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.promocodeService.getCurrentPromo();
    this.promocodeService.promocode$.pipe(takeUntil(this.destroy$)).subscribe((promocode) => {
      if (promocode && !promocode.rewarded) {
        this.promocodeForm.setValue({ code: promocode?.code || this.defaultPromocode });
      }
      this.cdr.markForCheck();
    });
    this.promocodeService.promocodeError$.pipe(takeUntil(this.destroy$)).subscribe((promocodeError) => {
      this.errorMessage = promocodeError?.message;
      this.cdr.markForCheck();
    });
    this.promocodeService.promocodeReward$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.reward = val;
      this.cdr.markForCheck();
    });
    this.promocodeService.promocodeStatus$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.promoStatus = val;
      if (this.promoStatus === PromoStatus.SUCCESS) {
        this.lastAppliedPromo = this.promocodeForm.controls.code.value;
        this.promocodeForm.markAsPristine();
      }
      this.cdr.markForCheck();
    });
    this.promocodeForm.controls.code.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      if (val === this.lastAppliedPromo) {
        this.promocodeForm.markAsPristine();
      }
      this.errorMessage = undefined;
      this.cdr.markForCheck();
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
  }
  activatePromocode(): void {
    if (this.promocodeForm.value.code) {
      this.promocodeService.activatePromoCode(this.promocodeForm.value.code);
    }
  }
  useDefPromo(): void {
    this.promocodeForm.setValue({ code: this.defaultPromocode });
  }
  activeClasses(status: PromoStatus, isCompact: boolean): string[] {
    const classes: string[] = [status];
    if (isCompact) {
      classes.push('compact');
    }
    return classes;
  }
  showIcon(promoStatus: PromoStatus): string | undefined {
    const code = this.promocodeForm.controls.code.value;
    if (code.length) {
      const valueSame = this.lastAppliedPromo === code;
      if (promoStatus === PromoStatus.SUCCESS && valueSame) {
        return 'checked-icon-round-bold';
      }
      if (promoStatus === PromoStatus.ERROR && this.errorMessage) {
        return 'notification-attention';
      }
    }
    return;
  }
  isButtonDisabled(formInvalid: boolean, actionStatus: StateActionStatus.DISPATCH | StateActionStatus.SUCCESS | null): boolean {
    return formInvalid || actionStatus === StateActionStatus.DISPATCH;
  }
  bonusAmountConstructor(reward: IPromocodeReward | null): BonusAmount {
    return bonusAmountConstructorUtil(reward);
  }
  errorMessageConstructor(errorMessage: string | undefined): string {
    return errorMessageConstructorUtil(errorMessage);
  }
}
